import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import useUpdateVariantStatus from 'workflow/CampaignSummary/ResultsTable/api/mutations/useUpdateVariantStatus'
import { ResponseVariant } from 'workflow/Insights/Insights'
import { ApprovedBy, BanditStatus } from 'workflow/interface'
import {
  fetchCampaignData,
  updateResultsVariantStatus,
} from 'workflow/Workflow.actions'

import { useAppDispatch, useAppSelector } from 'common/hooks/redux'
import {
  useContent,
  useSelectedComponentElement,
} from 'features/unifiedFlow/contentPage/hooks'
import { getWsTopic } from 'features/unifiedFlow/contentPage/hooks/websocketMessage/helpers'

import {
  calculateStatusCounts,
  filterByStatus,
  getVariantId,
  VariantStatus,
} from '../utils/variantUtils'

import { useCommonHandlers } from './useCommonHandlers'

export type ResponseVariantExtended = ResponseVariant & {
  isLoading: boolean
  isRefreshed: boolean
  category: string | undefined
  isHighlighted: boolean
  isSelected?: boolean
  variant_id?: string
  bandit_status?: BanditStatus & {
    status_updated_at?: string
  }
  isHoldout?: boolean
  combinationId?: string
  status?: 'approved' | 'dropped' | 'live' | 'pending'
  status_updated_at?: string
  approved_by?: ApprovedBy
}

type UseManageOptimizeVariantsProps = {
  statusFilter: VariantStatus
  onSelectVariant: (variant: ResponseVariant) => void
}

export const useManageOptimizeVariants = ({
  statusFilter,
  onSelectVariant,
}: UseManageOptimizeVariantsProps) => {
  const dispatch = useAppDispatch()
  const { content } = useContent()
  const { data: selectedElement } = useSelectedComponentElement()
  const [optimizeResults, setOptimizeResults] = useState<any[]>([])
  const cachedVariantsRef = useRef<ResponseVariantExtended[]>([])
  const subjectLines = useAppSelector(
    (state) => state.campaignStates.subjectLines
  )
  const isLoading = useAppSelector((state) => state.campaignStates.isLoading)

  useEffect(() => {
    const abortController = new AbortController()
    if (selectedElement?.campaign_id && !selectedElement?.personalization_id) {
      dispatch(
        fetchCampaignData(
          selectedElement.campaign_id,
          {},
          abortController.signal
        )
      )
    }
    return () => {
      abortController.abort()
    }
  }, [
    dispatch,
    selectedElement?.campaign_id,
    selectedElement?.personalization_id,
  ])

  useEffect(() => {
    setOptimizeResults(subjectLines)
  }, [subjectLines])

  const updateVariantStatus = useUpdateVariantStatus({
    campaignId: selectedElement?.campaign_id || '',
    updateResultsVariantStatus: (variantId: string, variantStatus: string) => {
      dispatch(updateResultsVariantStatus(variantId, variantStatus))
    },
  })

  const dropFn = useCallback(
    async (variantId: string): Promise<void> => {
      await updateVariantStatus.mutateAsync({
        id: variantId,
        variantStatus: 'dropped',
      })
    },
    [updateVariantStatus]
  )

  const onApproveSuccess = useCallback(
    (variantId: string) => {
      setOptimizeResults((prev) =>
        prev.map((v) =>
          getVariantId(v) === variantId
            ? {
                ...v,
                bandit_status: v.bandit_status
                  ? { ...v.bandit_status, status: 'live' }
                  : { status: 'live' },
              }
            : v
        )
      )

      if (selectedElement?.campaign_id) {
        dispatch(fetchCampaignData(selectedElement.campaign_id, {}, undefined))
      }
    },
    [dispatch, selectedElement?.campaign_id]
  )

  const onDropSuccess = useCallback((variantId: string) => {
    setOptimizeResults((prev) =>
      prev.map((v) =>
        getVariantId(v) === variantId
          ? {
              ...v,
              bandit_status: v.bandit_status
                ? { ...v.bandit_status, status: 'dropped' }
                : { status: 'dropped' },
            }
          : v
      )
    )
  }, [])

  const onBulkApproveSuccess = useCallback(
    (variantIds: string[]) => {
      setOptimizeResults((prev) =>
        prev.map((v) =>
          variantIds.includes(getVariantId(v))
            ? {
                ...v,
                bandit_status: v.bandit_status
                  ? { ...v.bandit_status, status: 'live' }
                  : { status: 'live' },
              }
            : v
        )
      )
      if (selectedElement?.campaign_id) {
        dispatch(fetchCampaignData(selectedElement.campaign_id, {}, undefined))
      }
    },
    [dispatch, selectedElement?.campaign_id]
  )

  const onBulkDropSuccess = useCallback((variantIds: string[]) => {
    setOptimizeResults((prev) =>
      prev.map((v) =>
        variantIds.includes(getVariantId(v))
          ? {
              ...v,
              bandit_status: v.bandit_status
                ? { ...v.bandit_status, status: 'dropped' }
                : { status: 'dropped' },
            }
          : v
      )
    )
  }, [])

  const variantsToBeApprovedFiltered = useMemo(() => {
    if (!content || !selectedElement) {
      return cachedVariantsRef.current
    }

    const sourceVariants: any[] = optimizeResults
    const mappedVariants: ResponseVariantExtended[] = sourceVariants
      .filter((variant) => {
        return filterByStatus(variant, statusFilter)
      })
      .map((variant) => {
        const id = getVariantId(variant)
        const tweakWsTopic = getWsTopic({
          action: 'tweakVariant',
          data: {
            contentId: content._id,
            elementId: selectedElement.element_id,
            variantId: id,
          },
        })
        const rejectWsTopic = getWsTopic({
          action: 'rejectVariant',
          data: {
            contentId: content._id,
            elementId: selectedElement.element_id,
            variantId: id,
          },
        })
        const isLoading =
          content.ws_messages?.some(
            (message: any) =>
              message.wsTopic === rejectWsTopic ||
              message.wsTopic === tweakWsTopic
          ) || false
        return {
          ...variant,
          _id: id,
          text: variant.text,
          status:
            variant.bandit_status?.status ||
            variant.status ||
            variant.variantStatus,
          isLoading,
          isRefreshed: false,
          category: undefined,
          isHighlighted: false,
          sentiments: variant.sentiments || {},
        }
      })

    const controlVariants: ResponseVariantExtended[] = []
    const regularVariants: ResponseVariantExtended[] = []

    mappedVariants.forEach((variant) => {
      const isControl = variant.ownsl === true
      if (isControl) {
        controlVariants.push(variant)
      } else {
        regularVariants.push(variant)
      }
    })

    const result = [...controlVariants, ...regularVariants]
    cachedVariantsRef.current = result
    return result
  }, [optimizeResults, statusFilter, content, selectedElement])

  const sharedHook = useCommonHandlers({
    variantsToBeApprovedFiltered,
    onSelectVariant,
    content,
    selectedElement,
    dropFn,
    onApproveSuccess,
    onDropSuccess,
    onBulkApproveSuccess,
    onBulkDropSuccess,
  })

  const statusCounts = useMemo(() => {
    return calculateStatusCounts(optimizeResults)
  }, [optimizeResults])

  return {
    ...sharedHook,
    variantsToBeApprovedFiltered,
    isLoading,
    statusCounts,
  }
}
